import React, { useState, useEffect } from 'react';
import Header from '../../Header';
import { useNotification } from '../NotificationProvider';
import './SeasonManagement.scss';

const SeasonManagement = () => {
    const [currentSeason, setCurrentSeason] = useState(null);
    const [seasons, setSeasons] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [showCloseModal, setShowCloseModal] = useState(false);
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [closing, setClosing] = useState(false);
    const [creating, setCreating] = useState(false);
    
    const { showSuccess, showError } = useNotification();
    
    const teamId = sessionStorage.getItem('selected_team_id');
    const teamType = sessionStorage.getItem('selected_team_type') || 'friendly';

    useEffect(() => {
        if (teamId) {
            fetchCurrentSeason();
            fetchAllSeasons();
        }
    }, [teamId]);

    const fetchCurrentSeason = async () => {
        try {
            const token = localStorage.getItem('auth_token');
            if (!token) {
                throw new Error('Authentication token not found');
            }

            const response = await fetch(
                `${process.env.REACT_APP_API_URL}/admin/get_current_season.php?team_id=${teamId}`,
                {
                    headers: {
                        'Authorization': `Bearer ${token}`
                    }
                }
            );

            if (!response.ok) {
                throw new Error('Failed to fetch current season');
            }

            const data = await response.json();
            if (!data.success) {
                throw new Error(data.message || 'Failed to fetch current season');
            }

            setCurrentSeason(data.season);
            // If season was auto-created, refresh the seasons list
            if (data.season && data.message && data.message.includes('created')) {
                fetchAllSeasons();
            }
        } catch (err) {
            console.error('Error fetching current season:', err);
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const fetchAllSeasons = async () => {
        try {
            const token = localStorage.getItem('auth_token');
            if (!token) {
                throw new Error('Authentication token not found');
            }

            const response = await fetch(
                `${process.env.REACT_APP_API_URL}/admin/get_seasons.php?team_id=${teamId}`,
                {
                    headers: {
                        'Authorization': `Bearer ${token}`
                    }
                }
            );

            if (!response.ok) {
                throw new Error('Failed to fetch seasons');
            }

            const data = await response.json();
            if (!data.success) {
                throw new Error(data.message || 'Failed to fetch seasons');
            }

            setSeasons(data.seasons || []);
        } catch (err) {
            console.error('Error fetching seasons:', err);
        }
    };

    const handleCloseSeason = async (formData) => {
        setClosing(true);
        try {
            const token = localStorage.getItem('auth_token');
            if (!token) {
                throw new Error('Authentication token not found');
            }

            const response = await fetch(
                `${process.env.REACT_APP_API_URL}/admin/close_season.php`,
                {
                    method: 'POST',
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'Content-Type': 'application/json'
                    },
                    body: JSON.stringify({
                        team_id: teamId,
                        end_date: formData.end_date,
                        new_season_name: formData.new_season_name || null,
                        new_season_start_date: formData.new_season_start_date || null
                    })
                }
            );

            const data = await response.json();
            if (!data.success) {
                throw new Error(data.message || 'Failed to close season');
            }

            showSuccess(data.message || 'Season closed successfully');
            setShowCloseModal(false);
            fetchCurrentSeason();
            fetchAllSeasons();
        } catch (err) {
            showError(err.message || 'Failed to close season');
        } finally {
            setClosing(false);
        }
    };

    const handleCreateSeason = async (formData) => {
        setCreating(true);
        try {
            const token = localStorage.getItem('auth_token');
            if (!token) {
                throw new Error('Authentication token not found');
            }

            const response = await fetch(
                `${process.env.REACT_APP_API_URL}/admin/create_season.php`,
                {
                    method: 'POST',
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'Content-Type': 'application/json'
                    },
                    body: JSON.stringify({
                        team_id: teamId,
                        name: formData.name,
                        start_date: formData.start_date
                    })
                }
            );

            const data = await response.json();
            if (!data.success) {
                throw new Error(data.message || 'Failed to create season');
            }

            showSuccess(data.message || 'Season created successfully');
            setShowCreateModal(false);
            fetchCurrentSeason();
            fetchAllSeasons();
        } catch (err) {
            showError(err.message || 'Failed to create season');
        } finally {
            setCreating(false);
        }
    };

    if (loading) {
        return (
            <div className="SeasonManagement container">
                <Header />
                <div className="loading">Loading season information...</div>
            </div>
        );
    }

    if (error && !currentSeason) {
        return (
            <div className="SeasonManagement container">
                <Header />
                <div className="error">{error}</div>
            </div>
        );
    }

    return (
        <div className="SeasonManagement container">
            <Header />
            <h1>Season Management</h1>

            {/* Current Season Section */}
            <div className="current-season-section">
                <h2>Current Season</h2>
                {currentSeason ? (
                    <div className="season-card active">
                        <div className="season-header">
                            <h3>{currentSeason.name}</h3>
                            <span className="badge active">Active</span>
                        </div>
                        <div className="season-info">
                            {teamType === 'friendly' ? (
                                <div className="auto-season-info">
                                    <span className="auto-indicator">🔄 Auto Renews</span>
                                    <p className="info-text">
                                        The next season will automatically start on <strong>1st January {new Date().getFullYear() + 1}</strong>. Previous year stats will be archived but players will be able to see past data.
                                    </p>
                                </div>
                            ) : (
                                <div className="auto-season-info">
                                    <span className="auto-indicator">🔄 Auto Renews</span>
                                    <p className="info-text">
                                        Squad and Academy teams follow the UK Football Season (August - July). The next season will automatically start on <strong>1st August {(() => {
                                            const currentDate = new Date();
                                            const currentMonth = currentDate.getMonth() + 1; // 1-12
                                            return currentMonth >= 8 ? currentDate.getFullYear() + 1 : currentDate.getFullYear();
                                        })()}</strong>. Previous year stats will be archived but players, parents and coaches will be able to see past data.
                                    </p>
                                </div>
                            )}
                        </div>
                        {teamType !== 'friendly' && (
                            <button 
                                className="btn-close-season"
                                onClick={() => setShowCloseModal(true)}
                            >
                                Close Season
                            </button>
                        )}
                    </div>
                ) : (
                    <div className="season-card no-season">
                        <p>No active season found.</p>
                        <button 
                            className="btn-create-season"
                            onClick={() => setShowCreateModal(true)}
                        >
                            Create New Season
                        </button>
                    </div>
                )}
            </div>

            {/* Historical Seasons Section */}
            <div className="historical-seasons-section">
                <h2>Historical Seasons</h2>
                {seasons.length > 0 ? (
                    <div className="seasons-list">
                        {seasons.map((season) => (
                            <div 
                                key={season.season_id} 
                                className="season-card archived"
                            >
                                <div className="season-header">
                                    <h3>{season.name}</h3>
                                    <span className="badge archived">Archived</span>
                                </div>
                                <div className="season-details">
                                    {season.closed_by_name && (
                                        <div className="detail-item">
                                            <span className="label">Closed By:</span>
                                            <span className="value">{season.closed_by_name}</span>
                                        </div>
                                    )}
                                    {season.closed_at_formatted && (
                                        <div className="detail-item">
                                            <span className="label">Closed At:</span>
                                            <span className="value">{season.closed_at_formatted}</span>
                                        </div>
                                    )}
                                    <div className="detail-item">
                                        <span className="label">Matches:</span>
                                        <span className="value">{season.match_count || 0}</span>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                ) : (
                    <p className="no-seasons">No historical seasons found.</p>
                )}
            </div>

            {/* Close Season Modal */}
            {showCloseModal && (
                <CloseSeasonModal
                    season={currentSeason}
                    onClose={() => setShowCloseModal(false)}
                    onConfirm={handleCloseSeason}
                    loading={closing}
                />
            )}

            {/* Create Season Modal */}
            {showCreateModal && (
                <CreateSeasonModal
                    onClose={() => setShowCreateModal(false)}
                    onConfirm={handleCreateSeason}
                    loading={creating}
                />
            )}
        </div>
    );
};

// Close Season Modal Component
const CloseSeasonModal = ({ season, onClose, onConfirm, loading }) => {
    const [endDate, setEndDate] = useState(new Date().toISOString().split('T')[0]);
    const [createNewSeason, setCreateNewSeason] = useState(true);
    const [newSeasonName, setNewSeasonName] = useState('');
    const [newSeasonStartDate, setNewSeasonStartDate] = useState('');
    
    const teamType = sessionStorage.getItem('selected_team_type') || 'friendly';

    useEffect(() => {
        if (season) {
            const currentDate = new Date();
            const currentYear = currentDate.getFullYear();
            const currentMonth = currentDate.getMonth() + 1; // 1-12
            
            // Calculate next season based on team type
            if (teamType === 'friendly') {
                // Friendly: Next calendar year
                const nextYear = currentYear + 1;
                setNewSeasonName(`${nextYear} Season`);
                setNewSeasonStartDate(`${nextYear}-01-01`);
            } else {
                // Squad/Academy: UK Football Season
                // If closing in Aug-Dec, next season starts Aug next year
                // If closing in Jan-Jul, next season starts Aug this year
                if (currentMonth >= 8) {
                    const nextYear = currentYear + 1;
                    setNewSeasonName(`${nextYear}-${nextYear + 1} Season`);
                    setNewSeasonStartDate(`${nextYear}-08-01`);
                } else {
                    setNewSeasonName(`${currentYear}-${currentYear + 1} Season`);
                    setNewSeasonStartDate(`${currentYear}-08-01`);
                }
            }
        }
    }, [season, teamType]);

    const handleSubmit = (e) => {
        e.preventDefault();
        onConfirm({
            end_date: endDate,
            new_season_name: createNewSeason ? newSeasonName : null,
            new_season_start_date: createNewSeason ? newSeasonStartDate : null
        });
    };

    return (
        <div className="modal-overlay" onClick={onClose}>
            <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                <h2>Close Season</h2>
                <form onSubmit={handleSubmit}>
                    <div className="form-group">
                        <label htmlFor="end_date">End Date *</label>
                        <input
                            type="date"
                            id="end_date"
                            value={endDate}
                            onChange={(e) => setEndDate(e.target.value)}
                            required
                        />
                    </div>

                    <div className="form-group checkbox-group">
                        <label>
                            <input
                                type="checkbox"
                                checked={createNewSeason}
                                onChange={(e) => setCreateNewSeason(e.target.checked)}
                            />
                            Create new season automatically
                        </label>
                    </div>

                    {createNewSeason && (
                        <>
                            <div className="form-group">
                                <label htmlFor="new_season_name">New Season Name *</label>
                                <input
                                    type="text"
                                    id="new_season_name"
                                    value={newSeasonName}
                                    onChange={(e) => setNewSeasonName(e.target.value)}
                                    required={createNewSeason}
                                    placeholder="e.g., 2025-2026 Season"
                                />
                            </div>

                            <div className="form-group">
                                <label htmlFor="new_season_start_date">New Season Start Date *</label>
                                <input
                                    type="date"
                                    id="new_season_start_date"
                                    value={newSeasonStartDate}
                                    onChange={(e) => setNewSeasonStartDate(e.target.value)}
                                    required={createNewSeason}
                                />
                            </div>
                        </>
                    )}

                    <div className="modal-actions">
                        <button type="button" onClick={onClose} disabled={loading}>
                            Cancel
                        </button>
                        <button type="submit" disabled={loading} className="btn-primary">
                            {loading ? 'Closing...' : 'Close Season'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

// Create Season Modal Component
const CreateSeasonModal = ({ onClose, onConfirm, loading }) => {
    const [name, setName] = useState('');
    const [startDate, setStartDate] = useState(new Date().toISOString().split('T')[0]);

    const handleSubmit = (e) => {
        e.preventDefault();
        onConfirm({ name, start_date: startDate });
    };

    return (
        <div className="modal-overlay" onClick={onClose}>
            <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                <h2>Create New Season</h2>
                <form onSubmit={handleSubmit}>
                    <div className="form-group">
                        <label htmlFor="name">Season Name *</label>
                        <input
                            type="text"
                            id="name"
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                            required
                            placeholder="e.g., 2025-2026 Season"
                        />
                    </div>

                    <div className="form-group">
                        <label htmlFor="start_date">Start Date *</label>
                        <input
                            type="date"
                            id="start_date"
                            value={startDate}
                            onChange={(e) => setStartDate(e.target.value)}
                            required
                        />
                    </div>

                    <div className="modal-actions">
                        <button type="button" onClick={onClose} disabled={loading}>
                            Cancel
                        </button>
                        <button type="submit" disabled={loading} className="btn-primary">
                            {loading ? 'Creating...' : 'Create Season'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default SeasonManagement;

